// Packages
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';

// Redux

import NumberFormat from 'react-number-format';

// Types
import { ITCOInputProps } from 'types';

// Utils
import { tcoFieldDecimalConfig } from 'utils';

// Styles
import '../TCOFieldStyles.scss';

export interface ITCOCustomInputProps extends ITCOInputProps {
  inputType: 'text' | 'number' | 'text-area' | 'dollar' | 'percent';
}

/** Custom input for 'text', 'text-area', 'text-copy', 'number-text', or 'number-copy' inputTypes on UCQ Field */
const TCOCustomInput = ({
  fieldInput,
  onFieldChange,
  inputType,
}: ITCOCustomInputProps) => {
  // =============================================
  // State/Refs/Hooks
  // =============================================
  const { id, value } = fieldInput;
  const [text, setText] = useState(value || '');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // =============================================
  // Helpers (Memo, CB, vars)
  // =============================================

  // =============================================
  // Interaction Handlers
  // =============================================

  const onChange = useCallback(
    (values: {
      formattedValue: string;
      value: string;
      floatValue?: number;
    }) => {
      const { value } = values;
      const formattedValue = value.replace(/\.$/, '');

      setText(formattedValue);
      onFieldChange(id, formattedValue);
    },
    [id, onFieldChange]
  );

  // =============================================
  // Render Methods
  // =============================================

  const input = useMemo(() => {
    const inputComponent =
      inputType === 'dollar' ? (
        <div
          className="kite-input kite-form-group"
          style={{ maxWidth: 'none' }}
        >
          <NumberFormat
            value={text}
            thousandSeparator={','}
            onValueChange={onChange}
            prefix={'$'}
            decimalScale={tcoFieldDecimalConfig[id] ?? 2}
            fixedDecimalScale={true}
            allowNegative={false}
            isNumericString
            className="kite-form-control kite-input__input"
            placeholder=""
          />
        </div>
      ) : (
        <div
          className="kite-input kite-form-group"
          style={{ maxWidth: 'none' }}
        >
          <NumberFormat
            getInputRef={(el: HTMLInputElement | null) =>
              (inputRef.current = el)
            }
            value={text}
            onValueChange={onChange}
            suffix="%"
            decimalScale={tcoFieldDecimalConfig[id] ?? 2}
            fixedDecimalScale={true}
            allowNegative={false}
            isAllowed={(values) => {
              const { floatValue } = values;
              return (
                floatValue === undefined ||
                (floatValue >= 0 && floatValue <= 100)
              );
            }}
            isNumericString
            className="kite-form-control kite-input__input"
            min={0}
            max={100}
            placeholder=""
          />
        </div>
      );

    return inputComponent;
  }, [id, inputType, onChange, text]);

  // =============================================
  // Effects
  // =============================================

  useEffect(() => {
    setText(value || '');
  }, [value]);

  // =============================================
  // Early Return (no precheck)
  // =============================================

  // =============================================
  // Return
  // =============================================
  return <div>{input}</div>;
};

export default TCOCustomInput;
